/**
 * The sync lifecycle: consent, sign-in, engine, status.
 *
 * Everything sync needs already existed and none of it was connected — the engine,
 * the transport, the identity and the consent gate were each built and tested with
 * no caller. This is the caller.
 *
 * ## The order is a legal requirement, not a preference
 *
 * Consent is checked before anything else happens, and re-checked whenever the
 * relay list or the stored consent changes. Three states stop sync rather than
 * warn about it — `none`, `outdated`, `relays-changed` — because continuing to
 * process special-category data under a consent that no longer covers it is
 * processing without a basis, whatever the screen says.
 *
 * Nothing here touches the network until `consentAllowsSync` is true. Signing in
 * IS network activity: it publishes the blinded alias to a relay.
 *
 * ## Locking stops sync
 *
 * The identity is sealed under the data key, so a locked app cannot load it. That
 * falls out of the design rather than being enforced separately, and it is the
 * behaviour we want: a locked device should not be talking to a relay about a
 * history it cannot itself read.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { addDays, today } from '@domain/date'
import type { ISODate } from '@domain/types'
import {
  consentAllowsSync,
  consentStatus,
  createGunSyncTransport,
  createSyncEngine,
  deriveSoulKey,
  deriveSyncSealingKey,
  loadRelays,
  loadSyncIdentity,
  readSyncConsent,
  updatePeers,
  getAllEntries,
  gunLoginWithIdentity,
  setEntryPusher,
  type SyncEngine,
  type SyncStatus,
} from '@shared/data/private'
import { useEntries } from './EntryContext'

export interface SyncContextValue {
  status: SyncStatus
  /** Re-read consent and identity, and start or stop accordingly. */
  refresh: () => Promise<void>
}

const SyncContext = createContext<SyncContextValue | undefined>(undefined)

/**
 * How far back to watch for remote changes.
 *
 * Souls are HMACs, so there is nothing to enumerate: a device can only ask about
 * dates it can name. That is the privacy property working as designed, and its
 * cost is that catching up is bounded by a window rather than being complete.
 *
 * A year plus a margin covers what the app actually uses — prediction reads the
 * last six cycles, and the insights screen a year. Reaching further would mean
 * thousands of subscriptions on a phone for data no screen displays.
 *
 * The window is the same for every user and every device, which matters: a
 * variable one would make the number of souls requested a signal about how much
 * history someone has.
 */
const WATCH_DAYS = 400

function datesToWatch(entries: readonly { date: ISODate }[]): ISODate[] {
  const dates = new Set<ISODate>()

  // Days already held locally, so an edit made on the other device to a day this
  // one knows about arrives even if it falls outside the window.
  for (const entry of entries) dates.add(entry.date)

  /*
   * Built with the domain date helpers, never with `toISOString()`.
   *
   * `toISOString` converts to UTC first, so west of Greenwich it yields
   * tomorrow's date for most of the evening. Here that would mean subscribing to
   * a soul for the wrong day — a device that syncs everything except the entry
   * the user just made, which is both the most visible day and the hardest
   * failure to attribute to a timezone.
   */
  const start = today()
  for (let i = 0; i < WATCH_DAYS; i += 1) dates.add(addDays(start, -i))

  return [...dates]
}

const DEBUG_SYNC = String(import.meta.env?.VITE_SYNC_DEBUG ?? '') === '1'

let catchUpDone = false

export function SyncProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SyncStatus>({ kind: 'off' })
  /*
   * Inbound records go through `putMergedEntry`, which writes to the database and
   * to the store's in-memory cache — but not to the entry provider's React state,
   * which is what every screen actually renders from.
   *
   * Without this, a record arriving from the other device would be stored
   * correctly and stay invisible until the next reload. The screen would be
   * wrong, the data would be right, and nothing would look broken.
   */
  const { reload } = useEntries()
  const engineRef = useRef<SyncEngine | undefined>(undefined)
  const unwatchRef = useRef<(() => void) | undefined>(undefined)
  const throttleTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const pendingStatusRef = useRef<SyncStatus | undefined>(undefined)

  const teardown = useCallback(() => {
    setEntryPusher(undefined)
    unwatchRef.current?.()
    unwatchRef.current = undefined
    engineRef.current?.stop()
    engineRef.current = undefined
    setStatus({ kind: 'off' })
    updatePeers([]) // Stop network reconnects when sync is off
    catchUpDone = false // Reset catch-up flag when sync is torn down
    
    // Clear throttled status updates
    if (throttleTimeoutRef.current !== undefined) {
      clearTimeout(throttleTimeoutRef.current)
      throttleTimeoutRef.current = undefined
    }
    pendingStatusRef.current = undefined
  }, [])

  const refresh = useCallback(async () => {
    // Always torn down first. Restarting without this would leave the previous
    // engine subscribed and still pushing under the old identity — which after a
    // passphrase change is the wrong account entirely.
    teardown()

    const list = await loadRelays()
    const consent = consentStatus(
      await readSyncConsent(),
      list.relays.map((relay) => new URL(relay.url).host),
    )
    if (!consentAllowsSync(consent)) return

    // Update Gun's active peer list with the consented relays
    const peerUrls = list.relays.map((relay) => relay.url)
    updatePeers(peerUrls)

    const identity = await loadSyncIdentity()
    // No identity while consent is valid means the app is locked, since the
    // identity is sealed under the data key. Not an error — sync resumes on
    // unlock, when this runs again.
    if (identity === undefined) return

    const signedIn = await gunLoginWithIdentity(identity)
    if (!signedIn.ok) return

    const engine = createSyncEngine({
      transport: createGunSyncTransport(),
      soulKey: await deriveSoulKey(identity.syncKeyMaterial),
      sealingKey: await deriveSyncSealingKey(identity.syncKeyMaterial),
    })

    engineRef.current = engine
    let lastState: 'offline' | 'pending' | 'idle' | 'off' | undefined = undefined

    engine.onStatusChange((next, event) => {
      const prevState = lastState
      lastState = next.kind

      // If we just reconnected to peer relays and have pending items, trigger a flush
      if (prevState === 'offline' && next.kind === 'pending') {
        void engine.flush().catch(() => undefined)
      }

      // The engine announces on every inbound merge as well as on every status
      // change. Arriving data becomes visible immediately, while status updates are throttled.
      if (event?.merged) {
        void reload()
      }

      pendingStatusRef.current = next

      if (throttleTimeoutRef.current === undefined) {
        throttleTimeoutRef.current = setTimeout(() => {
          if (pendingStatusRef.current !== undefined) {
            setStatus(pendingStatusRef.current)
            pendingStatusRef.current = undefined
          }
          throttleTimeoutRef.current = undefined
        }, 150)
      }
    })
    setStatus(engine.status())

    // Registered before watching, so an entry written during the initial catch-up
    // is still offered to the engine.
    setEntryPusher((entry) => engine.push(entry))

    const entries = await getAllEntries()
    unwatchRef.current = await engine.watch(datesToWatch(entries))

    // Perform catch-up push of existing entries if not done yet in this session
    if (!catchUpDone) {
      catchUpDone = true
      if (DEBUG_SYNC) {
        console.log("[SyncContext] Starting catch-up push for existing entries:", entries.length)
      }
      for (const entry of entries) {
        void engine.push(entry)
      }
    }
  }, [teardown, reload])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh().catch(() => undefined)
    return teardown
  }, [refresh, teardown])

  const value = useMemo<SyncContextValue>(() => ({ status, refresh }), [status, refresh])

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>
}

export function useSync(): SyncContextValue {
  const value = useContext(SyncContext)
  if (value === undefined) throw new Error('useSync used outside SyncProvider')
  return value
}
